import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ArrowUpRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

export function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [size, setSize] = useState(product.sizes[0]);
  const [pos, setPos] = useState({ x: 50, y: 50 });

  const quickAdd = () => {
    if (product.has_variants) {
      navigate(`/product/${product.slug}`);
      toast.message("Choose your shea variant to continue");
      return;
    }
    addItem({
      product_id: product.id,
      name: product.name,
      size: size.size,
      variant: null,
      price: size.price,
      image: product.image,
      quantity: 1,
    });
    toast.success(`${product.name} added`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1], delay: index * 0.1 }}
      className="group flex flex-col"
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/product/${product.slug}`} className="block">
        <div
          className="relative overflow-hidden rounded-sm bg-cream-2 aspect-[4/5]"
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            setPos({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
          }}
        >
          <div
            className="pointer-events-none absolute inset-0 z-10 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
            style={{ background: `radial-gradient(320px circle at ${pos.x}% ${pos.y}%, rgba(200,157,82,0.28), transparent 60%)` }}
          />
          <img
            src={product.image}
            alt={product.name}
            className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
          />
          <span className="absolute left-4 top-4 z-20 rounded-full bg-cream/85 backdrop-blur px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-moss">
            {product.badge}
          </span>
          <span className="absolute right-4 top-4 z-20 flex items-center gap-1 rounded-full bg-moss/85 px-2.5 py-1 text-[11px] text-cream">
            <Star className="h-3 w-3 fill-gold text-gold" /> {product.rating}
          </span>
        </div>
      </Link>

      <div className="mt-5 flex-1">
        <div className="flex items-start justify-between gap-3">
          <Link to={`/product/${product.slug}`}>
            <h3 className="font-serif text-2xl leading-tight text-moss group-hover:text-gold transition-colors">{product.name}</h3>
          </Link>
          <span className="mt-1 font-serif text-2xl text-moss whitespace-nowrap">${size.price.toFixed(0)}</span>
        </div>
        <p className="mt-2 text-sm text-muted leading-relaxed">{product.short_description}</p>

        <div className="mt-4 flex flex-wrap gap-2">
          {product.sizes.map((s) => (
            <button
              key={s.sku}
              onClick={() => setSize(s)}
              data-testid={`card-size-${product.slug}-${s.size.replace(/\s/g, "")}`}
              className={`rounded-full border px-4 py-1.5 text-xs font-mono transition-colors ${
                size.sku === s.sku ? "border-gold bg-gold text-moss-dark" : "border-[#D8CFBE] text-moss hover:border-gold"
              }`}
            >
              {s.size}
            </button>
          ))}
        </div>

        <div className="mt-5 flex gap-3">
          <button
            onClick={quickAdd}
            className="btn-gold flex-1 rounded-sm py-3 text-xs font-semibold uppercase tracking-wider"
            data-testid={`quick-add-${product.slug}`}
          >
            {product.has_variants ? "Choose Options" : "Add to Cart"}
          </button>
          <Link
            to={`/product/${product.slug}`}
            className="flex items-center justify-center rounded-sm border border-moss px-4 text-moss hover:bg-moss hover:text-cream transition-colors"
            data-testid={`view-product-${product.slug}`}
          >
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
